import type { Session } from '@halo/chat-sdk';
import { MessageComposerReplyPreview, useEditorContext } from '@halo/ui-chat-react';
import { memo, useCallback, useMemo } from 'react';
import { useAppStore } from '@halo/chat-sdk-react';
import { useStoreEnhance } from '@halo/zustand-enhance';
import { MsgsReplyItem } from '../messages/msgs-reply-item';

export interface MsgReplyPreviewChangeEvent {
  isEditing: boolean;
  editChanged: boolean;
}

export interface MsgReplyPreviewProps {
  session: Session;
  onChange?: (event: MsgReplyPreviewChangeEvent) => void;
}

export const MsgReplyPreview = memo(function MsgReplyPreview({
  session,
  onChange,
}: MsgReplyPreviewProps) {
  const { sessionStores } = useAppStore();
  const { editor } = useEditorContext();

  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const sessionStoreInstance = useMemo(() => {
    return sessionStores[session.chat_id];
  }, [session.chat_id, sessionStores]);

  const sessionStore = useStoreEnhance(sessionStoreInstance);

  const user = useMemo(() => {
    return sessionStore?.draftMessage?.replyMsgUser;
  }, [sessionStore?.draftMessage?.replyMsgUser]);

  const message = useMemo(() => {
    return sessionStore?.draftMessage?.replyMsg;
  }, [sessionStore?.draftMessage?.replyMsg]);

  const { isEditing, editChanged } = useMemo(() => {
    return {
      isEditing: Boolean(sessionStore?.draftMessage?.isEditing),
      editChanged:
        JSON.stringify(sessionStore?.draftMessage?.replyMsg?.payload) !==
        sessionStore?.draftMessage?.payload,
    };
  }, [sessionStore?.draftMessage]);

  const onClose = useCallback(() => {
    const payload = sessionStore?.draftMessage?.payload;
    editor.clear();
    sessionStore?.setDraftMessage(undefined);
    sessionStore?.setDraftMessage({ payload });
  }, [sessionStore, editor]);

  useEffect(() => {
    onChangeRef.current?.({ isEditing, editChanged });
  }, [isEditing, editChanged, onChange]);

  return (
    <MessageComposerReplyPreview
      message={message}
      user={user}
      isEditing={isEditing}
      onClose={onClose}
    >
      <MsgsReplyItem session={session} message={message} />
    </MessageComposerReplyPreview>
  );
});
